import { Panel, PageHeader } from "@/components/admin-shell";
import { apiRequest } from "@/lib/api";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useState, useEffect } from "react";
import { toast } from "sonner";
import { Save, Shield } from "lucide-react";

interface ISettings {
  pixels?: {
    ga4?: string;
    fbPixel?: string;
    gtm?: string;
    tiktok?: string;
  };
  scripts?: {
    header?: string;
    body?: string;
    footer?: string;
  };
}

interface IOrder {
  _id: string;
  total: number;
  status: string;
  createdAt: string;
}

const channels = [
  { name: "Organic Search", visits: 14820, share: 42, color: "oklch(0.78 0.12 85)" },
  { name: "Direct", visits: 7240, share: 21, color: "oklch(0.55 0.18 25)" },
  { name: "Facebook / IG", visits: 6480, share: 18, color: "oklch(0.68 0.08 85)" },
  { name: "Email", visits: 3940, share: 11, color: "oklch(0.45 0.14 25)" },
  { name: "Other", visits: 2820, share: 8, color: "oklch(0.85 0.06 85)" },
];

export default function AnalyticsPage() {
  const queryClient = useQueryClient();

  // Fetch Settings (for pixels and scripts)
  const { data: settings, isLoading: isSettingsLoading } = useQuery<ISettings>({
    queryKey: ["settings"],
    queryFn: () => apiRequest<ISettings>("/settings"),
  });

  // Fetch Orders (for live volume stats)
  const { data: orders = [], isLoading: isOrdersLoading } = useQuery<IOrder[]>({
    queryKey: ["orders"],
    queryFn: () => apiRequest<IOrder[]>("/orders"),
  });

  // Update Settings Mutation
  const updateSettingsMutation = useMutation({
    mutationFn: (payload: Partial<ISettings>) =>
      apiRequest<ISettings>("/settings", "PATCH", payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["settings"] });
      toast.success("Analytics configuration saved");
    },
    onError: (err: any) => {
      toast.error(err.message || "Failed to save configuration");
    },
  });

  // Form State
  const [ga4, setGa4] = useState("");
  const [fbPixel, setFbPixel] = useState("");
  const [gtm, setGtm] = useState("");
  const [tiktok, setTiktok] = useState("");

  const [headerScript, setHeaderScript] = useState("");
  const [bodyScript, setBodyScript] = useState("");
  const [footerScript, setFooterScript] = useState("");

  useEffect(() => {
    if (settings) {
      setGa4(settings.pixels?.ga4 || "");
      setFbPixel(settings.pixels?.fbPixel || "");
      setGtm(settings.pixels?.gtm || "");
      setTiktok(settings.pixels?.tiktok || "");

      setHeaderScript(settings.scripts?.header || "");
      setBodyScript(settings.scripts?.body || "");
      setFooterScript(settings.scripts?.footer || "");
    }
  }, [settings]);

  const handleSave = () => {
    updateSettingsMutation.mutate({
      pixels: {
        ga4,
        fbPixel,
        gtm,
        tiktok,
      },
      scripts: {
        header: headerScript,
        body: bodyScript,
        footer: footerScript,
      },
    });
  };

  // Group orders by last 7 days dynamically
  const daysOfWeek = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
  const salesByDay = Array.from({ length: 7 }).map((_, i) => {
    const d = new Date();
    d.setDate(d.getDate() - (6 - i));
    const dayName = daysOfWeek[d.getDay()];
    const dateStr = d.toISOString().split("T")[0];

    const dayOrders = orders.filter((o) => {
      if (!o.createdAt) return false;
      const orderDate = new Date(o.createdAt);
      return (
        orderDate.getFullYear() === d.getFullYear() &&
        orderDate.getMonth() === d.getMonth() &&
        orderDate.getDate() === d.getDate()
      );
    });
    const revenue = dayOrders.reduce((sum, o) => sum + (o.status !== "cancelled" ? o.total : 0), 0);

    return {
      day: dayName,
      orders: dayOrders.length,
      revenue,
    };
  });

  const maxOrders = Math.max(...salesByDay.map((d) => d.orders), 1);
  const totalRevenue = orders.reduce((sum, o) => sum + (o.status !== "cancelled" ? o.total : 0), 0);
  const avgOrderValue = orders.length > 0 ? Math.round(totalRevenue / orders.length) : 0;

  const fmt = (n: number) => `৳${n.toLocaleString("en-BD")}`;

  return (
    <>
      <PageHeader
        title="Analytics & Scripts"
        subtitle="Server-side tracking, pixel integrations, conversion metrics."
      />

      <div className="grid gap-6 lg:grid-cols-3">
        <Panel
          className="lg:col-span-2"
          title="Orders volume"
          description="Last 7 days dynamic distribution"
        >
          <div className="flex h-56 items-stretch gap-3 pt-6">
            {salesByDay.map((d) => (
              <div key={d.day} className="flex flex-1 flex-col items-center gap-2 group">
                <div className="w-full flex-1 flex items-end relative">
                  <div
                    className="w-full rounded-t bg-analytics-bar-gradient border-t border-gold/40 transition-all group-hover:opacity-85"
                    style={{ height: `${(d.orders / maxOrders) * 100}%` }}
                  />
                  <div className="absolute -top-7 left-1/2 -translate-x-1/2 rounded bg-card px-2 py-0.5 text-[10px] text-gold opacity-0 group-hover:opacity-100 transition whitespace-nowrap">
                    {d.orders} orders
                  </div>
                </div>
                <div className="text-[11px] uppercase tracking-widest text-muted-foreground">
                  {d.day}
                </div>
              </div>
            ))}
          </div>
        </Panel>

        <Panel title="Traffic by channel">
          <div className="space-y-3">
            {channels.map((c) => (
              <div key={c.name}>
                <div className="flex justify-between text-xs">
                  <span className="text-foreground">{c.name}</span>
                  <span className="text-muted-foreground">
                    {c.visits.toLocaleString()} · {c.share}%
                  </span>
                </div>
                <div className="mt-1 h-1.5 overflow-hidden rounded-full bg-accent">
                  <div className="h-full" style={{ width: `${c.share}%`, background: c.color }} />
                </div>
              </div>
            ))}
          </div>
        </Panel>
      </div>

      <div className="mt-6 grid gap-6 lg:grid-cols-2">
        <Panel title="Pixel & tracking integrations">
          <div className="space-y-3 text-sm">
            <div>
              <label className="text-xs uppercase tracking-widest text-gold-soft">
                Google Analytics 4 Measurement ID
              </label>
              <input
                value={ga4}
                onChange={(e) => setGa4(e.target.value)}
                placeholder="G-XXXXXX"
                className="mt-1 w-full rounded border border-gold/25 bg-accent/25 px-3 py-2 text-foreground outline-none focus:border-gold"
              />
            </div>
            <div>
              <label className="text-xs uppercase tracking-widest text-gold-soft">
                Facebook Pixel ID
              </label>
              <input
                value={fbPixel}
                onChange={(e) => setFbPixel(e.target.value)}
                placeholder="1234567890"
                className="mt-1 w-full rounded border border-gold/25 bg-accent/25 px-3 py-2 text-foreground outline-none focus:border-gold"
              />
            </div>
            <div>
              <label className="text-xs uppercase tracking-widest text-gold-soft">
                Google Tag Manager Container ID
              </label>
              <input
                value={gtm}
                onChange={(e) => setGtm(e.target.value)}
                placeholder="GTM-XXXXXX"
                className="mt-1 w-full rounded border border-gold/25 bg-accent/25 px-3 py-2 text-foreground outline-none focus:border-gold"
              />
            </div>
            <div>
              <label className="text-xs uppercase tracking-widest text-gold-soft">
                TikTok Pixel ID
              </label>
              <input
                value={tiktok}
                onChange={(e) => setTiktok(e.target.value)}
                placeholder="TikTok Pixel ID"
                className="mt-1 w-full rounded border border-gold/25 bg-accent/25 px-3 py-2 text-foreground outline-none focus:border-gold"
              />
            </div>
          </div>
        </Panel>

        <Panel
          title="Script Injection"
          description="Inject custom analytics/tracking tags to layout"
        >
          <div className="space-y-3">
            <div>
              <label className="text-xs uppercase tracking-widest text-gold-soft">
                Header scripts
              </label>
              <textarea
                rows={2}
                value={headerScript}
                onChange={(e) => setHeaderScript(e.target.value)}
                placeholder="<!-- Custom JS or Meta Verification inside <head> -->"
                className="mt-1 w-full rounded border border-gold/25 bg-background/40 px-3 py-2 font-mono text-xs text-foreground outline-none focus:border-gold"
              />
            </div>
            <div>
              <label className="text-xs uppercase tracking-widest text-gold-soft">
                Body scripts
              </label>
              <textarea
                rows={2}
                value={bodyScript}
                onChange={(e) => setBodyScript(e.target.value)}
                placeholder="<!-- Custom JS immediately after <body> opens -->"
                className="mt-1 w-full rounded border border-gold/25 bg-background/40 px-3 py-2 font-mono text-xs text-foreground outline-none focus:border-gold"
              />
            </div>
            <div>
              <label className="text-xs uppercase tracking-widest text-gold-soft">
                Footer scripts
              </label>
              <textarea
                rows={2}
                value={footerScript}
                onChange={(e) => setFooterScript(e.target.value)}
                placeholder="<!-- Custom JS or pixels before </body> closes -->"
                className="mt-1 w-full rounded border border-gold/25 bg-background/40 px-3 py-2 font-mono text-xs text-foreground outline-none focus:border-gold"
              />
            </div>
            <div className="text-right">
              <button
                onClick={handleSave}
                disabled={updateSettingsMutation.isPending}
                className="inline-flex items-center gap-2 rounded-md bg-gold px-4 py-2 text-xs font-semibold text-primary-foreground hover:opacity-90 disabled:opacity-50 cursor-pointer"
              >
                <Save className="h-3.5 w-3.5" />
                {updateSettingsMutation.isPending ? "Saving..." : "Save Analytics Config"}
              </button>
            </div>
          </div>
        </Panel>
      </div>

      <div className="mt-6 grid gap-4 sm:grid-cols-4">
        {[
          { l: "Conv. rate", v: "3.84%" },
          { l: "Avg. order value", v: fmt(avgOrderValue) },
          { l: "Bounce rate", v: "38.2%" },
          { l: "ROAS", v: "4.7x" },
        ].map((s) => (
          <Panel key={s.l} className="p-5">
            <div className="text-[10px] uppercase tracking-[0.3em] text-gold-soft">{s.l}</div>
            <div className="mt-2 font-display text-2xl">{s.v}</div>
          </Panel>
        ))}
      </div>
    </>
  );
}
